import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, AlertTriangle, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Party, PartyGroup } from '@/app/types/party';
import { listParties, listPartyGroups, getParty } from '@/app/services/partyService';
import { debounce } from 'lodash';
import { PaginatedResponse } from '@/app/types/api';

interface TechnicianSelectorProps {
  value: number | undefined;
  onChange: (value: number | undefined, technician?: Party) => void;
  placeholder?: string;
  disabled?: boolean;
  technicianGroupName?: string; // Party group name to filter (defaults to "TECHNICIAN")
  allowClear?: boolean;
}

export const TechnicianSelector: React.FC<TechnicianSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select technician...',
  disabled = false,
  technicianGroupName = 'TECHNICIAN',
  allowClear = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSetSearchQuery = useMemo(() => debounce(setSearchQuery, 300), []);

  // Fetch party groups to get the technician group ID
  const technicianGroupQuery = useQuery<PaginatedResponse<PartyGroup>, Error>({
    queryKey: ['partyGroupsForTechnician', technicianGroupName],
    queryFn: () =>
      listPartyGroups({
        filters: JSON.stringify([{ field: 'name', op: 'ilike', value: technicianGroupName }]),
        limit: 1,
      }),
    staleTime: 15 * 60 * 1000,
    gcTime: Infinity,
  });

  const technicianGroupId = useMemo(() => {
    return technicianGroupQuery.data?.items[0]?.id;
  }, [technicianGroupQuery.data]);

  const buildFilters = (): string | undefined => {
    const filters: { field: string; op: string; value: unknown }[] = [];
    filters.push({ field: 'is_active', op: 'eq', value: true });
    if (technicianGroupId) {
      filters.push({ field: 'party_group_id', op: 'eq', value: technicianGroupId });
    }
    return filters.length > 0 ? JSON.stringify(filters) : undefined;
  };

  const techniciansQuery = useQuery<PaginatedResponse<Party>, Error>({
    queryKey: ['technicians', { filters: buildFilters(), search: searchQuery, limit: 100 }],
    queryFn: () =>
      listParties({
        filters: buildFilters(),
        search_query: searchQuery || undefined,
        limit: 100,
        sort_by: 'name',
        sort_dir: 'asc',
      }),
    // Fetch when open, have value, or when we have the group ID (or group query is done)
    enabled: (open || !!value) && (!!technicianGroupId || technicianGroupQuery.isSuccess),
    staleTime: 5 * 60 * 1000,
  });

  // Separate query to fetch the selected technician if not in the list
  const selectedTechnicianQuery = useQuery<Party, Error>({
    queryKey: ['technician', value],
    queryFn: () => getParty(value!),
    enabled: !!value && !techniciansQuery.data?.items.find((t) => t.id === value),
    staleTime: 5 * 60 * 1000,
  });

  const technicians = techniciansQuery.data?.items ?? [];
  const selectedTechnician = useMemo(() => {
    // First try to find in the technicians list
    const techFromList = technicians.find((t) => t.id === value);
    if (techFromList) return techFromList;

    // If not found in list, use the individually fetched technician
    return selectedTechnicianQuery.data;
  }, [technicians, value, selectedTechnicianQuery.data]);

  // Ensure the selected technician is in the dropdown list
  const displayTechnicians = useMemo(() => {
    if (!selectedTechnician || technicians.find((t) => t.id === selectedTechnician.id)) {
      return technicians;
    }
    // Add the selected technician to the beginning of the list if it's not there
    return [selectedTechnician, ...technicians];
  }, [technicians, selectedTechnician]);

  const isLoading = technicianGroupQuery.isLoading || techniciansQuery.isLoading;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', !value && 'text-muted-foreground')}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            <Wrench className="h-4 w-4 shrink-0" />
            {selectedTechnician
              ? selectedTechnician.name
              : value && (isLoading || selectedTechnicianQuery.isLoading)
                ? 'Loading...'
                : placeholder}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search technician..."
            onValueChange={debouncedSetSearchQuery}
            disabled={disabled}
          />
          <CommandList>
            {isLoading && (
              <div className="p-2 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {techniciansQuery.isError && (
              <div className="p-2 text-center text-sm text-destructive flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 mr-2" /> Error loading technicians
              </div>
            )}
            {!isLoading &&
              !techniciansQuery.isError &&
              technicians.length === 0 &&
              !searchQuery && <CommandEmpty>No technicians found.</CommandEmpty>}
            {!isLoading &&
              !techniciansQuery.isError &&
              technicians.length === 0 &&
              searchQuery && (
                <CommandEmpty>No technicians match &quot;{searchQuery}&quot;.</CommandEmpty>
              )}

            {!isLoading && !techniciansQuery.isError && (
              <ScrollArea className="max-h-52">
                <CommandGroup>
                  {allowClear && value && (
                    <CommandItem
                      value="__clear__"
                      onSelect={() => {
                        onChange(undefined, undefined);
                        setOpen(false);
                        setSearchQuery('');
                      }}
                      className="text-muted-foreground"
                    >
                      <span className="italic">Clear selection</span>
                    </CommandItem>
                  )}
                  {displayTechnicians.map((technician) => (
                    <CommandItem
                      key={technician.id}
                      value={technician.name}
                      onSelect={() => {
                        onChange(technician.id, technician);
                        setOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === technician.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{technician.name}</span>
                        {technician.phone && (
                          <span className="text-xs text-muted-foreground">{technician.phone}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
